/**
 * MarkingWorkspace.jsx — Phase 2C C2: Criterion Scoring Workspace
 *
 * Full marking environment for a single evaluation_case.
 *
 * Acceptance rules enforced:
 *   1. Only the assigned marker's own round is loaded/editable
 *   2. Attempt evidence displayed left; rubric scoring right
 *   3. Criterion scores write only to the actor's own round
 *   4. Autosave (1500ms debounce) on score / commentary change
 *   5. Total = sum of criterion scores; reconciled against max_mark_snapshot
 *   6. Feedback: MARKER_ONLY / MODERATION / RATIFICATION / STUDENT_RELEASE
 *   7. Before submit: fully editable
 *   8. After submit: ALL inputs locked + LOCKED banner
 *   9. PRIMARY marker only sees their own round (anchoring filter via API)
 *  10. SECOND marker: primary awarded_mark masked while their round is open
 *  11. Student 403 via EvaluationShell
 *  12. All state/tenant/relationship checks enforced server-side
 *
 * Negative proof (C2.N1): submitting a PATCH on a SUBMITTED round → 409
 *   → UI surfaces "Round is already submitted — no further changes are permitted."
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Lock,
  CheckCircle2,
  AlertTriangle,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Save,
  Send,
  X,
  Eye,
  EyeOff,
  FileText,
  ClipboardList,
  Info,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { EvaluationShell } from "../components/evaluation/EvaluationShell";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() {
  return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` };
}
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    headers: { ...authH(), "Content-Type": "application/json" },
    ...opts,
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw Object.assign(
      new Error(body?.detail?.message || (typeof body?.detail === "string" ? body.detail : null) || r.statusText),
      { status: r.status, detail: body?.detail }
    );
  }
  return body;
}

const VISIBILITY_OPTIONS = [
  { value: "MARKER_ONLY",    label: "Marker Only",    desc: "Only you and coordinator" },
  { value: "MODERATION",     label: "Moderation",     desc: "Markers + moderator + coordinator" },
  { value: "RATIFICATION",   label: "Ratification",   desc: "All parties up to ratifier" },
  { value: "STUDENT_RELEASE",label: "Student Release",desc: "Published to student after ratification" },
];

const ROLE_MAP = {
  PRIMARY_MARKER: "Primary Marker",
  SECOND_MARKER:  "Second Marker",
  MODERATOR:      "Moderator",
  COORDINATOR:    "Coordinator",
};

// ─── Round status banner ─────────────────────────────────────────────────────

function RoundStatusBanner({ round, anchoringBias }) {
  if (!round?.id) return null;

  if (round.status === "SUBMITTED") {
    return (
      <div
        data-testid="round-locked-banner"
        className="flex items-center gap-3 px-5 py-3.5 rounded-xl border border-teal-800/60 bg-teal-950/30 mb-5"
      >
        <div className="w-8 h-8 rounded-full bg-teal-900/40 border border-teal-800/40 flex items-center justify-center flex-shrink-0">
          <Lock className="w-4 h-4 text-teal-400" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-teal-300">Round Submitted — Locked</div>
          <div className="text-xs text-teal-500 mt-0.5">
            This round is finalized. No further changes are permitted. Awarded:{" "}
            <strong className="text-teal-300">{round.awarded_mark ?? "—"} / {round.max_mark_snapshot ?? "—"}</strong>
          </div>
        </div>
        {round.submitted_at && (
          <span className="text-xs text-slate-500 whitespace-nowrap">
            {new Date(round.submitted_at).toLocaleString()}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 px-5 py-3 rounded-xl border border-sky-800/40 bg-sky-950/20 mb-5">
      <div className="w-7 h-7 rounded-full bg-sky-900/40 flex items-center justify-center flex-shrink-0">
        <ClipboardList className="w-3.5 h-3.5 text-sky-400" />
      </div>
      <div className="flex-1">
        <div className="text-xs font-semibold text-sky-300">Round In Progress — Autosave Active</div>
        {anchoringBias && (
          <div className="flex items-center gap-1 text-xs text-amber-400 mt-0.5">
            <EyeOff className="w-3 h-3" />
            Independent marking: primary result hidden until you submit.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Criterion Score Row ──────────────────────────────────────────────────────

function CriterionRow({ criterion, score, onChange, locked, showPerformanceLevels }) {
  const [expanded, setExpanded] = useState(false);
  const levels = criterion.performance_levels;

  const scoreNum = parseFloat(score?.score_value ?? "");
  const isValid = !isNaN(scoreNum) && scoreNum >= 0 && scoreNum <= parseFloat(criterion.max_score);
  const pct = isValid ? (scoreNum / parseFloat(criterion.max_score)) * 100 : 0;

  return (
    <div
      data-testid={`criterion-row-${criterion.id}`}
      className="border border-slate-700/60 rounded-xl overflow-hidden mb-3"
    >
      {/* Header */}
      <div className="flex items-start gap-3 px-4 py-3 bg-slate-800/40">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-500 bg-slate-800 border border-slate-700 px-1.5 py-0.5 rounded">
              {criterion.code}
            </span>
            <span className="text-sm font-semibold text-slate-100">{criterion.title}</span>
          </div>
          {criterion.description && (
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{criterion.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Score input */}
          <div className="flex items-center gap-1.5">
            <input
              data-testid={`score-input-${criterion.id}`}
              type="number"
              min={0}
              max={parseFloat(criterion.max_score)}
              step={0.5}
              value={score?.score_value ?? ""}
              onChange={(e) => onChange("score_value", e.target.value)}
              disabled={locked}
              className={`w-16 text-center py-1.5 rounded-lg border text-sm font-mono transition-colors ${
                locked
                  ? "bg-slate-800/40 border-slate-700/40 text-slate-500 cursor-not-allowed"
                  : isValid && score?.score_value !== ""
                  ? "bg-sky-950/40 border-sky-700/60 text-sky-200 focus:outline-none focus:border-sky-500"
                  : "bg-slate-900 border-slate-700 text-slate-200 focus:outline-none focus:border-sky-600"
              }`}
              placeholder="—"
            />
            <span className="text-xs text-slate-500">/ {criterion.max_score}</span>
          </div>
          {levels && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-slate-600 hover:text-slate-400 transition-colors"
              title="Performance levels"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {isValid && score?.score_value !== "" && (
        <div className="h-0.5 bg-slate-800">
          <div
            data-testid={`score-bar-${criterion.id}`}
            className={`h-full transition-all duration-300 ${pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-sky-500" : "bg-amber-500"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {/* Commentary */}
      <div className="px-4 py-3 bg-slate-900/40">
        <textarea
          data-testid={`commentary-input-${criterion.id}`}
          value={score?.marker_commentary ?? ""}
          onChange={(e) => onChange("marker_commentary", e.target.value)}
          disabled={locked}
          placeholder={locked ? "(No commentary)" : "Add marker commentary for this criterion…"}
          rows={2}
          className={`w-full bg-transparent border-none text-xs text-slate-400 placeholder:text-slate-600 focus:outline-none resize-none ${locked ? "cursor-not-allowed opacity-60" : ""}`}
        />
      </div>

      {/* Performance levels (expandable) */}
      {expanded && levels && (
        <div className="px-4 py-3 border-t border-slate-700/40 bg-slate-900/60">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 mb-2">
            Performance Levels
          </div>
          <div className="space-y-1.5">
            {Object.entries(levels).map(([lvl, desc]) => (
              <div key={lvl} className="flex items-start gap-2">
                <span className="text-[10px] font-bold text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded min-w-[50px] text-center">
                  {lvl}
                </span>
                <span className="text-xs text-slate-500 leading-relaxed">{desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Feedback Panel ───────────────────────────────────────────────────────────

function FeedbackPanel({ caseId, existingFeedback, locked, onAdded }) {
  const [form, setForm] = useState({ visibility: "MARKER_ONLY", content: "" });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleAdd = async () => {
    if (!form.content.trim()) { toast.error("Feedback content required"); return; }
    setSaving(true);
    try {
      await apiFetch("/evaluation/feedback", {
        method: "POST",
        body: JSON.stringify({ evaluation_case_id: caseId, ...form }),
      });
      toast.success("Feedback added");
      setForm({ visibility: "MARKER_ONLY", content: "" });
      onAdded?.();
    } catch (e) {
      toast.error("Feedback failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-5 rounded-xl border border-slate-700/60" data-testid="feedback-panel">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-xs font-semibold text-slate-400 hover:text-slate-200 transition-colors"
      >
        <MessageSquare className="w-3.5 h-3.5" />
        Feedback
        <span className="ml-1 text-slate-600">({existingFeedback.length})</span>
        <span className="ml-auto">{expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-700/40">
          {/* Existing feedback */}
          {existingFeedback.length > 0 && (
            <div className="space-y-2 mb-4">
              {existingFeedback.map((fb) => (
                <div key={fb.id} data-testid={`feedback-item-${fb.id}`} className="flex items-start gap-2.5 text-xs">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border whitespace-nowrap ${
                    fb.visibility === "MARKER_ONLY"     ? "bg-slate-800 text-slate-400 border-slate-700" :
                    fb.visibility === "MODERATION"      ? "bg-amber-950/50 text-amber-400 border-amber-800/40" :
                    fb.visibility === "RATIFICATION"    ? "bg-purple-950/50 text-purple-400 border-purple-800/40" :
                                                          "bg-emerald-950/50 text-emerald-400 border-emerald-800/40"
                  }`}>
                    {fb.visibility?.replace(/_/g, " ")}
                  </span>
                  <span className="text-slate-300 flex-1">{fb.content}</span>
                </div>
              ))}
            </div>
          )}

          {/* Add feedback form */}
          {!locked && (
            <div className="space-y-2.5">
              <select
                data-testid="feedback-visibility-select"
                value={form.visibility}
                onChange={(e) => setForm((f) => ({ ...f, visibility: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-300 focus:outline-none focus:border-sky-600"
              >
                {VISIBILITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{`${opt.label} — ${opt.desc}`}</option>
                ))}
              </select>
              <textarea
                data-testid="feedback-content-input"
                value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                placeholder="Add feedback for this evaluation case…"
                rows={3}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-300 placeholder:text-slate-600 focus:outline-none focus:border-sky-600 resize-none"
              />
              <button
                data-testid="add-feedback-btn"
                onClick={handleAdd}
                disabled={saving || !form.content.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium disabled:opacity-50 transition-colors"
              >
                {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
                Add Feedback
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Submit Confirmation Modal ────────────────────────────────────────────────

function SubmitConfirmModal({ open, onClose, onConfirm, scores, criteria, totalAwarded, totalMax, submitting }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        data-testid="submit-confirm-modal"
        className="relative w-[440px] bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-slate-300">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-amber-900/40 border border-amber-800/40 flex items-center justify-center flex-shrink-0">
            <Send className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-100">Submit & Finalize Round</h3>
            <p className="text-xs text-slate-400 mt-1">
              Once submitted, this round is <strong className="text-amber-400">permanently locked</strong> and cannot be edited.
            </p>
          </div>
        </div>

        {/* Score summary */}
        <div className="rounded-lg border border-slate-700/60 bg-slate-800/60 p-4 mb-5 space-y-2">
          <div className="text-xs font-semibold text-slate-400 mb-2">Score Summary</div>
          {criteria.map((crit) => {
            const sc = scores[crit.id];
            return (
              <div key={crit.id} className="flex items-center justify-between text-xs">
                <span className="text-slate-400 truncate max-w-[220px]">{crit.title}</span>
                <span className="text-slate-200 font-mono">
                  {sc?.score_value ?? "—"} / {crit.max_score}
                </span>
              </div>
            );
          })}
          <div className="pt-2 mt-2 border-t border-slate-700/40 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-200">Total Awarded</span>
            <span className={`text-base font-bold font-mono ${totalAwarded === totalMax ? "text-emerald-400" : "text-sky-400"}`}>
              {totalAwarded} / {totalMax}
            </span>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="confirm-submit-btn"
            onClick={onConfirm}
            disabled={submitting}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium disabled:opacity-50 transition-colors"
          >
            {submitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Confirm Submit
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function MarkingWorkspace() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scores keyed by criterion_id → { score_value, marker_commentary }
  const [scores, setScores] = useState({});
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [lockError, setLockError] = useState(null); // set when 409 on submitted round

  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [openingRound, setOpeningRound] = useState(false);

  const autosaveTimer = useRef(null);

  // ── Load context ────────────────────────────────────────────────────────────
  const loadContext = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`/evaluation/cases/${caseId}/marking-context`);
      setCtx(data);

      // Hydrate scores from existing criterion_score rows
      if (data.actor_scores?.length > 0) {
        const s = {};
        data.actor_scores.forEach((sc) => {
          s[sc.rubric_criterion_id] = {
            score_value: sc.score_value != null ? String(sc.score_value) : "",
            marker_commentary: sc.marker_commentary || "",
          };
        });
        setScores(s);
      } else {
        // Init empty scores for each criterion
        const s = {};
        (data.criteria || []).forEach((c) => { s[c.id] = { score_value: "", marker_commentary: "" }; });
        setScores(s);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { loadContext(); }, [loadContext]);

  // ── Open round ──────────────────────────────────────────────────────────────
  const handleOpenRound = async () => {
    if (!ctx?.assignment_id) { toast.error("No assignment found for your user on this case"); return; }
    setOpeningRound(true);
    try {
      const roleNum = ctx.actor_marker_role === "SECOND_MARKER" ? 2
                    : ctx.actor_marker_role === "MODERATOR"     ? 3 : 1;
      await apiFetch(`/evaluation/cases/${caseId}/rounds`, {
        method: "POST",
        body: JSON.stringify({
          marker_assignment_id: ctx.assignment_id,
          round_number: roleNum,
          marker_role: ctx.actor_marker_role,
        }),
      });
      toast.success("Marking round opened");
      await loadContext();
    } catch (e) {
      toast.error("Failed to open round: " + e.message);
    } finally {
      setOpeningRound(false);
    }
  };

  // ── Autosave ────────────────────────────────────────────────────────────────
  const triggerAutosave = useCallback(
    (newScores) => {
      if (!ctx?.actor_round?.id || ctx.actor_round.status === "SUBMITTED") return;
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      autosaveTimer.current = setTimeout(async () => {
        setSaveStatus("saving");
        try {
          const scoreList = Object.entries(newScores)
            .filter(([_, v]) => v.score_value !== "")
            .map(([criterionId, v]) => ({
              rubric_criterion_id: criterionId,
              score_value: parseFloat(v.score_value),
              marker_commentary: v.marker_commentary || null,
            }));

          await apiFetch(`/evaluation/rounds/${ctx.actor_round.id}/scores`, {
            method: "PATCH",
            body: JSON.stringify({ scores: scoreList }),
          });
          setSaveStatus("saved");
          setTimeout(() => setSaveStatus("idle"), 2000);
        } catch (e) {
          if (e.status === 409) {
            // Round was submitted → show lock error
            setLockError("This round has already been submitted. No further changes are permitted.");
            await loadContext();
          } else {
            setSaveStatus("error");
          }
        }
      }, 1500);
    },
    [ctx, loadContext]
  );

  const handleScoreChange = (criterionId, field, value) => {
    if (ctx?.actor_round?.status === "SUBMITTED") {
      setLockError("This round has already been submitted. No further changes are permitted.");
      return;
    }
    setLockError(null);
    const newScores = { ...scores, [criterionId]: { ...scores[criterionId], [field]: value } };
    setScores(newScores);
    triggerAutosave(newScores);
  };

  // ── Total calculation ────────────────────────────────────────────────────────
  const totalMax = (ctx?.criteria || []).reduce((sum, c) => sum + parseFloat(c.max_score || 0), 0);
  const totalAwarded = (ctx?.criteria || []).reduce((sum, c) => {
    const v = parseFloat(scores[c.id]?.score_value ?? "");
    return sum + (isNaN(v) ? 0 : v);
  }, 0);
  const totalPct = totalMax > 0 ? (totalAwarded / totalMax) * 100 : 0;

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!ctx?.actor_round?.id) return;
    setSubmitting(true);
    try {
      // Final save first
      const scoreList = Object.entries(scores)
        .filter(([_, v]) => v.score_value !== "")
        .map(([criterionId, v]) => ({
          rubric_criterion_id: criterionId,
          score_value: parseFloat(v.score_value),
          marker_commentary: v.marker_commentary || null,
        }));
      await apiFetch(`/evaluation/rounds/${ctx.actor_round.id}/scores`, {
        method: "PATCH",
        body: JSON.stringify({ scores: scoreList }),
      });
      // Submit round
      const result = await apiFetch(`/evaluation/rounds/${ctx.actor_round.id}/submit`, { method: "POST" });
      toast.success(`Round submitted — ${result.awarded_mark} / ${result.max_mark_snapshot} awarded`);
      setShowSubmitModal(false);
      await loadContext();
    } catch (e) {
      if (e.status === 409) {
        setLockError("Submission rejected: " + (e.detail?.message || e.message));
      } else {
        toast.error("Submit failed: " + e.message);
      }
      setShowSubmitModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  const isRoundLocked = ctx?.actor_round?.status === "SUBMITTED";

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <EvaluationShell breadcrumb="Marking">
        <div className="flex items-center justify-center py-20 gap-3 text-slate-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading marking workspace…</span>
        </div>
      </EvaluationShell>
    );
  }

  if (error) {
    return (
      <EvaluationShell breadcrumb="Marking">
        <div className="flex items-center gap-3 p-6 rounded-xl border border-red-800/50 bg-red-950/20 text-red-300 text-sm">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div>
            <strong>Cannot open workspace:</strong> {error}
            <div className="text-xs text-red-400/70 mt-1">
              Ensure you are assigned to this case and the case is in a markable state.
            </div>
          </div>
        </div>
      </EvaluationShell>
    );
  }

  const { case: evalCase, rubric, criteria = [], attempt_meta, evidence = [], actor_round, feedback = [],
          actor_marker_role, assignment_id, anchoring_bias_active } = ctx;

  return (
    <EvaluationShell
      breadcrumb="Marking"
      title={
        <span className="flex items-center gap-3">
          <button
            data-testid="back-to-queue-btn"
            onClick={() => navigate("/assessment-evaluation/marking")}
            className="text-slate-500 hover:text-slate-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          Marking Workspace
        </span>
      }
    >
      {/* Context bar */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <span className="text-xs text-slate-500 font-mono">Case: {caseId?.slice(0, 20)}…</span>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
          evalCase?.status === "MARKING_IN_PROGRESS" ? "bg-sky-950 text-sky-300 border-sky-800/60" :
          evalCase?.status === "MARKED"              ? "bg-teal-950 text-teal-300 border-teal-800/60" :
          evalCase?.status === "SECOND_MARK_REQUIRED"? "bg-amber-950 text-amber-300 border-amber-800/60" :
          "bg-slate-800 text-slate-400 border-slate-700"
        }`}>
          {evalCase?.status?.replace(/_/g, " ")}
        </span>
        <span className="text-xs text-indigo-400 font-medium">
          {ROLE_MAP[actor_marker_role] || actor_marker_role}
        </span>
        {actor_round?.id && (
          <span className="text-xs text-slate-500">
            Round #{actor_round.round_number}
            {actor_round.status === "SUBMITTED" && (
              <span className="ml-1.5 text-teal-400">· Submitted</span>
            )}
          </span>
        )}
        {/* Autosave indicator */}
        <span className="ml-auto flex items-center gap-1.5 text-xs">
          {saveStatus === "saving" && <><RefreshCw className="w-3 h-3 animate-spin text-slate-500" /><span className="text-slate-500">Saving…</span></>}
          {saveStatus === "saved"  && <><CheckCircle2 className="w-3 h-3 text-emerald-500" /><span className="text-emerald-500">Saved</span></>}
          {saveStatus === "error"  && <><AlertTriangle className="w-3 h-3 text-red-400" /><span className="text-red-400">Save failed</span></>}
        </span>
      </div>

      {/* Lock error banner (shown when 409 received on a submitted round) */}
      {lockError && (
        <div
          data-testid="lock-error-banner"
          className="flex items-start gap-3 px-4 py-3 rounded-xl border border-red-800/60 bg-red-950/20 text-red-300 text-sm mb-5"
        >
          <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">{lockError}</div>
          <button onClick={() => setLockError(null)} className="text-slate-500 hover:text-slate-300">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* 2-column workspace */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_440px] gap-6">

        {/* ── LEFT: Attempt Evidence ── */}
        <div className="min-w-0" data-testid="evidence-panel">
          <div className="rounded-xl border border-slate-800 overflow-hidden">
            <div className="px-5 py-3.5 bg-slate-900/60 border-b border-slate-800 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-300">Attempt Evidence</span>
              {anchoring_bias_active && (
                <span className="ml-auto flex items-center gap-1 text-xs text-amber-400 border border-amber-800/40 bg-amber-950/20 px-2 py-0.5 rounded-full">
                  <EyeOff className="w-3 h-3" />
                  Independent Marking
                </span>
              )}
            </div>

            <div className="p-5 space-y-4">
              {/* Attempt metadata */}
              {attempt_meta?.id ? (
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-slate-600 mb-0.5">Student</div>
                    <div className="text-slate-300 font-mono">{attempt_meta.student_id?.slice(0, 16)}…</div>
                  </div>
                  <div>
                    <div className="text-slate-600 mb-0.5">Submitted</div>
                    <div className="text-slate-300">
                      {attempt_meta.submitted_at ? new Date(attempt_meta.submitted_at).toLocaleString() : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-600 mb-0.5">Attempt #</div>
                    <div className="text-slate-300">{attempt_meta.attempt_number}</div>
                  </div>
                  <div>
                    <div className="text-slate-600 mb-0.5">Late</div>
                    <div className={attempt_meta.late_submission ? "text-amber-400" : "text-slate-400"}>
                      {attempt_meta.late_submission ? "Yes" : "No"}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-600 italic">No attempt metadata available.</div>
              )}

              {/* Responses */}
              {evidence.length > 0 ? (
                <div className="space-y-4">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Responses ({evidence.length})
                  </div>
                  {evidence.map((resp, idx) => (
                    <div
                      key={resp.id}
                      data-testid={`response-item-${idx}`}
                      className="rounded-lg border border-slate-700/60 overflow-hidden"
                    >
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-800/40 text-xs">
                        <span className="text-slate-600">Q{idx + 1}</span>
                        <span className="text-slate-500">{resp.interaction_type}</span>
                        {resp.is_final && (
                          <span className="ml-auto text-emerald-500 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Final
                          </span>
                        )}
                      </div>
                      <div className="px-3 py-3 bg-slate-900/40 text-xs text-slate-300 leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {resp.response_data?.text || resp.response_data?.answer || 
                         JSON.stringify(resp.response_data, null, 2)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  data-testid="no-responses-notice"
                  className="flex flex-col items-center justify-center py-8 text-slate-600"
                >
                  <FileText className="w-8 h-8 mb-2 opacity-30" />
                  <div className="text-xs">No submitted responses on record for this attempt.</div>
                  <div className="text-xs mt-1 text-slate-700">Score based on any external materials provided.</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT: Scoring Panel ── */}
        <div className="xl:sticky xl:top-0 xl:self-start" data-testid="scoring-panel">
          <div className="rounded-xl border border-slate-800 overflow-hidden">
            <div className="px-5 py-3.5 bg-slate-900/60 border-b border-slate-800 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-300">
                {rubric?.title || "Rubric Scoring"}
              </span>
              {isRoundLocked && (
                <span className="ml-auto flex items-center gap-1 text-xs text-teal-400">
                  <Lock className="w-3 h-3" /> Locked
                </span>
              )}
            </div>

            <div className="p-5">
              {/* Round status */}
              <RoundStatusBanner round={actor_round} anchoringBias={anchoring_bias_active} />

              {/* Open round button (before first round) */}
              {!actor_round?.id && assignment_id && !isRoundLocked && (
                <button
                  data-testid="open-round-btn"
                  onClick={handleOpenRound}
                  disabled={openingRound}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-sky-700/60 bg-sky-950/30 text-sky-300 text-sm font-medium hover:bg-sky-900/30 disabled:opacity-50 transition-colors mb-5"
                >
                  {openingRound ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />}
                  Open Marking Round
                </button>
              )}

              {/* No assignment */}
              {!assignment_id && !user?.role?.includes("admin") && (
                <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-950/20 border border-amber-800/30 rounded-lg px-3 py-2 mb-4">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  You do not have an active marking assignment for this case.
                </div>
              )}

              {/* Criteria */}
              {criteria.length > 0 && (actor_round?.id || actor_round?.status === "SUBMITTED") ? (
                <>
                  {criteria.map((criterion) => (
                    <CriterionRow
                      key={criterion.id}
                      criterion={criterion}
                      score={scores[criterion.id]}
                      onChange={(field, value) => handleScoreChange(criterion.id, field, value)}
                      locked={isRoundLocked}
                    />
                  ))}

                  {/* Total reconciliation */}
                  <div
                    data-testid="total-reconciliation"
                    className="mt-4 rounded-xl border border-slate-700/60 p-4 bg-slate-900/40"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-400">Total Awarded</span>
                      <span className={`text-lg font-bold font-mono ${
                        totalPct >= 80 ? "text-emerald-400" : totalPct >= 50 ? "text-sky-400" : "text-amber-400"
                      }`}>
                        {totalAwarded.toFixed(1)} / {totalMax}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                      <div
                        data-testid="total-score-bar"
                        className={`h-full rounded-full transition-all duration-500 ${
                          totalPct >= 80 ? "bg-emerald-500" : totalPct >= 50 ? "bg-sky-500" : "bg-amber-500"
                        }`}
                        style={{ width: `${Math.min(100, totalPct)}%` }}
                      />
                    </div>
                    <div className="text-right text-xs text-slate-600 mt-1">{totalPct.toFixed(0)}%</div>
                  </div>

                  {/* Feedback */}
                  <FeedbackPanel
                    caseId={caseId}
                    existingFeedback={feedback}
                    locked={isRoundLocked}
                    onAdded={loadContext}
                  />

                  {/* Submit button */}
                  {!isRoundLocked && actor_round?.id && (
                    <button
                      data-testid="submit-round-btn"
                      onClick={() => setShowSubmitModal(true)}
                      disabled={Object.values(scores).every((s) => s.score_value === "")}
                      className="w-full mt-5 flex items-center justify-center gap-2 py-3 rounded-xl bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white text-sm font-semibold transition-colors"
                    >
                      <Send className="w-4 h-4" />
                      Submit & Finalize Round
                    </button>
                  )}
                </>
              ) : (
                criteria.length === 0 ? (
                  <div className="text-xs text-slate-600 italic py-4 text-center">
                    No rubric criteria found for this component.
                  </div>
                ) : (
                  !actor_round?.id && !assignment_id && (
                    <div className="text-xs text-slate-600 italic py-4 text-center">
                      Open a marking round to begin scoring.
                    </div>
                  )
                )
              )}
            </div>
          </div>

          {/* Integrity note */}
          <div className="mt-3 flex items-start gap-2 text-[10px] text-slate-600 px-1">
            <ShieldCheck className="w-3 h-3 flex-shrink-0 mt-0.5" />
            <span>
              Scores are written only to your own round. SOD and state checks are enforced server-side.
              Submitted rounds are immutable.
            </span>
          </div>
        </div>
      </div>

      {/* Submit confirmation modal */}
      <SubmitConfirmModal
        open={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onConfirm={handleSubmit}
        scores={scores}
        criteria={criteria}
        totalAwarded={totalAwarded}
        totalMax={totalMax}
        submitting={submitting}
      />
    </EvaluationShell>
  );
}
